import isMobile from "ismobilejs";

/**
 * @class
 * @memberOf P3
 */
export default class View {
    /**
     * @param {Object} [options]
     * @param {boolean} [options.width=0]
     * @param {number} [options.height=0]
     * @param {string} [options.holderId]
     * @param {boolean} [options.portrait=false]
     * @param {string} [options.rotateImageUrl]
     * @param {number} [options.rotateImageColor=0xFFFFFF]
     * @param {boolean} [options.aspectRatioFillHeight=true]
     */
    constructor(options) {
        /**
         * @member {Object}
         */
        this.signals = {};

        /**
         * @member {signals.Signal}
         */
        this.signals.ready = new signals.Signal();

        /**
         * @member {signals.Signal}
         */
        this.signals.resize = new signals.Signal();

        /**
         * @type {Object}
         * @private
         */
        this._options = Object.assign({}, {
            width: 0,
            height: 0,
            holderId: "game",
            portrait: false,
            rotateImageColor: 0xFFFFFF,
            aspectRatioFillHeight: true
        }, options);

        /**
         * @member {Element}
         * @private
         */
        this._holder = null;

        /**
         * @member {Element}
         * @private
         */
        this._canvas = null;

        /**
         * @member {Element}
         * @private
         */
        this._iosfix = null;

        /**
         * @member {Element}
         * @private
         */
        this._rotateImage = null;

        /**
         * @type {string}
         * @private
         */
        this._targetOrientation = this._options.portrait ? "portrait" : "landscape";
    }

    init() {
        if (document.readyState === "complete" || P3.utils.isCocoonJS()) {
            this._onLoad();
        } else {
            window.self.onload = () => {
                setTimeout(() => {
                    this._onLoad();
                }, 0);
            }
        }
    }

    /**
     */
    resize() {
        const width = isMobile.any ? document.documentElement.clientWidth : window.innerWidth;
        const height = isMobile.any ? document.documentElement.clientHeight : window.innerHeight;

        if (window.self === window.top) {
            this._holder.style.width = width + "px";
            this._holder.style.height = height + "px";
        } else {
            this._holder.style.width = "100%";
            this._holder.style.height = "100%";
        }

        if (isMobile.apple.device) {
            this._iosfix.style.height = parseInt(this._holder.style.height) + 1 + "px";
        }

        // this scroll is used in IOS 8.0+ to move the game down when the user clicks to show the scrollbar.
        window.scrollTo(0, 0);

        if (this._options.aspectRatioFillHeight) {
            let ratioW = width / this._options.width;
            let ratioH = this._options.height / height;

            this._updateDimensions(
                Math.floor((this._options.width * ratioW) * ratioH),
                this._options.height
            );
        } else {
            let ratioW = this._options.width / width;
            let ratioH = height / this._options.height;

            this._updateDimensions(
                this._options.width,
                Math.floor((this._options.height * ratioH) * ratioW)
            );
        }

        this.toggleRotate(!this.isCorrectOrientation);
        this.signals.resize.dispatch(this.isCorrectOrientation);
    }

    /**
     * @param {boolean} value
     */
    toggleRotate(value) {
        this._canvas.style.display = value ? "none" : "block";
        this._rotateImage.style.display = value ? "block" : "none";
    }

    /**
     * @private
     */
    _createHolder() {
        if (P3.utils.isCocoonJS()) return;

        this._holder = document.getElementById(this._options.holderId);
        if (!this._holder) {
            this._holder = document.createElement("div");
            this._holder.id = this._options.holderId;
            document.body.appendChild(this._holder);
        }

        this._holder.style.position = "absolute";
        this._holder.style.left = 0;
        this._holder.style.top = 0;
        this._holder.style.width = window.self.innerWidth + "px";
        this._holder.style.height = window.self.innerHeight + "px";
        View.holder = this._holder;

        // forces the browser bars to be hidden in iOS9
        if (isMobile.apple.device) {
            this._iosfix = document.createElement("div");
            this._iosfix.id = "iosfix";
            this._iosfix.style.position = "absolute";
            this._iosfix.style.left = 0;
            this._iosfix.style.right = 0;
            this._iosfix.style.top = 0;
            this._iosfix.style.bottom = 0;
            this._iosfix.style.width = "100%";
            this._iosfix.style.height = this._holder.height + 1;
            this._iosfix.style.visibility = "hidden";
            document.body.appendChild(this._iosfix);
            View.iosfix = this._iosfix;
        }
    }

    /**
     * @private
     */
    _createCanvas() {
        if (P3.utils.isCocoonJS()) {
            this._canvas = document.createElement("screencanvas");
            this._canvas.id = "canvas";
            this._canvas.width = this._options.width;
            this._canvas.height = this._options.height;
            this._canvas.style.cssText = "idtkscale:ScaleAspectFill;";
            document.body.appendChild(this._canvas);
        } else {
            this._canvas = document.createElement("canvas");
            this._canvas.id = "canvas";
            this._canvas.tabIndex = 1;
            this._canvas.style.position = "absolute";
            this._canvas.style.left = 0;
            this._canvas.style.right = 0;
            this._canvas.style.top = 0;
            this._canvas.style.bottom = 0;
            this._canvas.style.width = "100%";
            this._canvas.style.height = "100%";
            this._canvas.style.overflow = "visible";
            this._canvas.style.display = "block";
            this._holder.appendChild(this._canvas);
        }
        View.canvas = this._canvas;
    }

    /**
     * @private
     */
    _createRotateImage() {
        if (P3.utils.isCocoonJS()) return;

        this._rotateImage = document.createElement("div");
        this._rotateImage.id = "rotateImage";
        this._rotateImage.style.position = "absolute";
        this._rotateImage.style.left = 0;
        this._rotateImage.style.top = 0;

        this._rotateImage.style.width = "100%";
        this._rotateImage.style.height = "100%";
        this._rotateImage.style.marginLeft = "auto";
        this._rotateImage.style.marginRight = 0 + "auto";
        this._rotateImage.style.overflow = "visible";
        this._rotateImage.style.display = "none";
        this._rotateImage.style.zIndex = 1000;

        this._rotateImage.style.backgroundImage = this._options.rotateImageUrl ? "url(" + this._options.rotateImageUrl + ")" : "";
        this._rotateImage.style.backgroundColor = this._options.rotateImageColor || 0xFFFFFF;
        this._rotateImage.style.backgroundPosition = "50% 50%";
        this._rotateImage.style.backgroundRepeat = "no-repeat";
        this._rotateImage.style.backgroundSize = "100% auto";

        this._holder.appendChild(this._rotateImage);

        // prevent dragging the ui
        this._rotateImage.addEventListener("touchmove", function(event) {
            event.preventDefault();
            return false;
        });

        // prevent dragging the ui
        this._rotateImage.addEventListener("touchstart", function(event) {
            event.preventDefault();
            return false;
        });

        // prevent dragging the ui
        this._rotateImage.addEventListener("touchend", function(event) {
            event.preventDefault();
            return false;
        });
    }

    /**
     * @param {number} width
     * @param {number} height
     * @private
     */
    _updateDimensions(width, height) {
        View.width = this._width = Math.round(width);
        View.height = this._height = Math.round(height);
        View.center.x = Math.round(width * 0.5);
        View.center.y = Math.round(height * 0.5);
    }

    /**
     * @private
     */
    _disableInteractions() {
        // prevent dragging the ui
        View.canvas.addEventListener("touchmove", function(event) {
            event.preventDefault();
            return false;
        });

        // prevent dragging the ui
        View.canvas.addEventListener("touchstart", function(event) {
            event.preventDefault();
            return false;
        });

        // prevent dragging the ui
        View.canvas.addEventListener("touchend", function(event) {
            event.preventDefault();
            return false;
        });

        if (isMobile.android.device) {
            View.canvas.addEventListener("mousedown", function(event) {
                event.stopPropagation();
                event.preventDefault();
                event.stopImmediatePropagation();
            }, false);

            View.canvas.addEventListener("mouseup", function(event) {
                event.stopPropagation();
                event.preventDefault();
                event.stopImmediatePropagation();
            }, false);

            View.canvas.addEventListener("click", function(event) {
                event.stopPropagation();
                event.preventDefault();
                event.stopImmediatePropagation();
            }, false);
        }
    }

    /**
     * @private
     */
    _onLoad() {
        this._createHolder();
        this._createCanvas();
        this._createRotateImage();
        this._disableInteractions();

        if (P3.utils.isCocoonJS()) {
            this._updateDimensions(
                this._options.width * (window.innerWidth / this._options.width) / (window.innerHeight / this._options.height),
                this._options.height
            );
            this.signals.ready.dispatch(this._canvas);
        } else {
            if (isMobile.android.device) {
                window.self.addEventListener("orientationchange", this._onOrientationChange.bind(this), false);
            } else {
                window.self.addEventListener("resize", this._onResize.bind(this), false);
            }
            this.signals.ready.dispatch(this._canvas);

            this.resize();
        }
    }

    /**
     * @param {Event} event
     * @private
     */
    _onResize(event) {
        this.resize();
    }

    /**
     * @param {Event} event
     * @private
     */
    _onOrientationChange(event) {
        window.self.addEventListener("resize", listener, false);

        const that = this;
        function listener() {
            window.self.removeEventListener("resize", listener, false);
            that.resize();
        }
    }

    /**
     * @returns {Element}
     */
    get holder() {
        return this._holder;
    }

    /**
     * @returns {Object}
     */
    get options() {
        return this._options;
    }

    /**
     * @returns {string}
     */
    get orientation() {
        return this._width >= this._height ? "landscape" : "portrait";
    }

    /**
     * @returns {boolean}
     */
    get isCorrectOrientation() {
        return isMobile.any ? this.orientation === this._targetOrientation : true;
    }
}

/**
 * @type {Element}
 */
View.holder = null;

/**
 * @type {Element}
 */
View.canvas = null;

/**
 * @type {number}
 */
View.width = 0;

/**
 * @type {number}
 */
View.height = 0;

/**
 * @type {PIXI.Point}
 */
View.center = new PIXI.Point();
